import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Cpu } from 'lucide-react';
import { redisConfigurationService, type RedisConfiguration } from '../../../../components/api/redisConfigurationService';
import AddRedisConfigModal from './AddRedisConfigModal';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import VaultEntityCard from './VaultEntityCard';

const Cache: React.FC<{ isLight?: boolean; requestAdd?: number }> = ({ isLight = false, requestAdd = 0 }) => {
  const [list, setList] = useState<RedisConfiguration[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editConfig, setEditConfig] = useState<RedisConfiguration | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [configToDelete, setConfigToDelete] = useState<RedisConfiguration | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await redisConfigurationService.getList();
      setList(Array.isArray(res) ? res : []);
    } catch {
      setError('Failed to load Redis configurations.');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const lastAddTick = useRef(requestAdd);
  useEffect(() => {
    if (requestAdd === lastAddTick.current) return;
    lastAddTick.current = requestAdd;
    if (requestAdd < 1) return;
    setEditConfig(null);
    setShowModal(true);
  }, [requestAdd]);

  const muted = isLight ? 'text-neutral-500' : 'text-neutral-400';
  const openEdit = (cfg: RedisConfiguration) => {
    setEditConfig(cfg);
    setShowModal(true);
  };

  return (
    <div>
      {error ? <p className="mb-3 text-sm text-red-400">{error}</p> : null}

      {loading ? (
        <div className={`text-center py-10 text-sm ${muted}`}>Loading…</div>
      ) : list.length === 0 ? (
        <div className={`text-center py-10 text-sm ${muted}`}>No Redis configurations yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((cfg) => {
            const host = [cfg.host, cfg.port].filter((value) => value !== undefined && value !== '').join(':');
            const details = [host || null, cfg.database != null ? `db ${cfg.database}` : null, cfg.description]
              .filter(Boolean)
              .join(' · ');
            return (
              <VaultEntityCard
                key={cfg.id}
                isLight={isLight}
                title={cfg.name}
                description={details || 'No description provided.'}
                tag={cfg.isActive ? 'Active' : 'Inactive'}
                icon={Cpu}
                iconClass={isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/15 text-amber-300'}
                inactive={!cfg.isActive}
                onOpen={() => openEdit(cfg)}
                onEdit={() => openEdit(cfg)}
                onDelete={() => setConfigToDelete(cfg)}
                deleteDisabled={deletingId === cfg.id}
              />
            );
          })}
        </div>
      )}

      {showModal && (
        <AddRedisConfigModal
          onClose={() => { setShowModal(false); setEditConfig(null); }}
          onSubmit={fetchList}
          initialData={editConfig}
        />
      )}

      {configToDelete && (
        <ConfirmDialog
          title="Delete Redis Configuration"
          message={`Delete "${configToDelete.name}"? This cannot be undone.`}
          onYes={async () => {
            const id = configToDelete.id;
            setConfigToDelete(null);
            setDeletingId(id);
            setError(null);
            try {
              await redisConfigurationService.delete(id);
              await fetchList();
            } catch (e: any) {
              setError(e?.response?.data?.message || e?.message || 'Failed to delete Redis configuration.');
            } finally {
              setDeletingId(null);
            }
          }}
          onCancel={() => setConfigToDelete(null)}
        />
      )}
    </div>
  );
};

export default Cache;
